import { Box } from '@modusign/design-system';
import { useMemo, useRef, useState } from 'react';
import { useMount } from 'react-use';

/**
 * IE11에서 CSS object-fit을 지원하지 않아서
 * 컨테이너 크기에 맞추고 넘침을 방지하고자 작성한 컴포넌트입니다.
 */
export default function ContainedImage({
  containerSize,
  src,
}: {
  containerSize: { height: number; width: number };
  src: string;
}) {
  const [visibility, setVisibility] = useState<'hidden' | 'visible'>('hidden');

  const imageRef = useRef<HTMLImageElement>(null);

  const naturalHeight = imageRef.current?.naturalHeight ?? 0;
  const naturalWidth = imageRef.current?.naturalWidth ?? 0;

  const newWidth = containerSize.width;
  const newHeight = (naturalHeight / naturalWidth) * newWidth;

  const size: { height: string; width: string } = useMemo((): { height: string; width: string } => {
    const isOverflowHeight = newHeight > containerSize.height;
    const isOverflowWidth = newWidth > containerSize.width;

    const isVerticalImage = naturalHeight - naturalWidth > 0;
    const baseHeight = { height: '100%', width: 'auto' };
    const baseWidth = { height: 'auto', width: '100%' };

    if (isOverflowHeight && isOverflowWidth) {
      return isVerticalImage ? baseHeight : baseWidth;
    }

    if (isOverflowHeight) {
      return baseHeight;
    }

    return baseWidth;
  }, [containerSize.height, containerSize.width, naturalHeight, naturalWidth, newHeight, newWidth]);

  /**
   * 이미지 마운트할 때 컨테이너 영역을 넘치게 렌더링되고
   * 이벤트를 발생시켜야 다시 렌더링되는 이슈가 있어서 강제로 상태를 변경합니다.
   */
  useMount(() => {
    setVisibility('visible');
  });

  return (
    <Box
      alt=""
      as="img"
      ref={imageRef}
      src={src}
      sx={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        margin: 0,
        padding: 0,
        height: size.height,
        width: size.width,
        pointerEvents: 'none',
        userSelect: 'none',
        visibility,
      }}
    />
  );
}
